import React, { useEffect, useState } from 'react';
import { CheckIcon, LoaderIcon, MapPinIcon, PhoneIcon, ShieldAlertIcon, TriangleAlertIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Field, Input, Textarea } from '../ui/Field';
import { ChoiceGroup, Toggle } from '../ui/ChoiceGroup';
import { useSafety } from '../../contexts/SafetyContext';
import { responderForCampus } from '../../data/safety';
import type { AlertKind, EmergencyAlert } from '../../types/safety';
const KINDS: {
  key: AlertKind;
  label: string;
}[] = [{
  key: 'personal_safety',
  label: 'I feel unsafe'
}, {
  key: 'medical',
  label: 'Medical'
}, {
  key: 'fire',
  label: 'Fire'
}, {
  key: 'security',
  label: 'Being followed or threatened'
}, {
  key: 'other',
  label: 'Something else'
}];
interface EmergencyDialogProps {
  open: boolean;
  onClose: () => void;
  campusId: string;
  raisedBy: EmergencyAlert['raisedBy'];
  trustedContacts: {
    name: string;
    relationship: string;
    phone: string;
  }[];
  onRaised: (alert: EmergencyAlert) => void;
}
type Locating = 'idle' | 'locating' | 'found' | 'denied' | 'unsupported';

/**
 * Raising an alert is a consequential action, so it is deliberate: the student
 * sees who will receive it, whether that channel actually exists, and exactly
 * what will be shared — before anything is sent. The direct phone number is
 * shown first, because it works whether or not Campuzon does.
 */
export function EmergencyDialog({
  open,
  onClose,
  campusId,
  raisedBy,
  trustedContacts,
  onRaised
}: EmergencyDialogProps) {
  const {
    raise
  } = useSafety();
  const responder = responderForCampus(campusId);
  const [kind, setKind] = useState<string>(KINDS[0].label);
  const [shareLocation, setShareLocation] = useState(true);
  const [shareContacts, setShareContacts] = useState(true);
  const [placeHint, setPlaceHint] = useState('');
  const [note, setNote] = useState('');
  const [locating, setLocating] = useState<Locating>('idle');
  const [coordinates, setCoordinates] = useState<EmergencyAlert['coordinates']>(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open || !shareLocation || locating !== 'idle') return;
    if (!('geolocation' in navigator)) {
      setLocating('unsupported');
      return;
    }
    setLocating('locating');
    navigator.geolocation.getCurrentPosition(position => {
      setCoordinates({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracyMetres: Math.round(position.coords.accuracy)
      });
      setLocating('found');
    }, () => setLocating('denied'), {
      enableHighAccuracy: true,
      timeout: 8000
    });
  }, [open, shareLocation, locating]);
  const unreachable = !responder || responder.channel === 'none';
  return <Modal open={open} onClose={onClose} title="Raise an emergency alert" description="Read who this reaches before you send it." footer={<>
          <Button variant="secondary" onClick={onClose} data-id="element-4140" data-mp="1myq8o33xjwjq">
            Cancel
          </Button>
          <Button variant="primary" loading={sending} className="border-danger-700 bg-danger-700 hover:border-[#991b1b] hover:bg-[#991b1b]" onClick={() => {
      setSending(true);
      const alert = raise({
        kind: (KINDS.find(item => item.label === kind)?.key ?? 'other') as AlertKind,
        campusId,
        disclosure: {
          location: shareLocation && locating === 'found',
          trustedContacts: shareContacts,
          note: note.trim().length > 0
        },
        coordinates,
        placeHint: placeHint.trim(),
        note: note.trim(),
        raisedBy,
        trustedContacts: trustedContacts.map(contact => `${contact.name} (${contact.relationship})`)
      });
      setSending(false);
      onRaised(alert);
      onClose();
    }} data-id="element-4141" data-mp="1qza6l8wbv46x">
            {unreachable ? 'Record alert anyway' : 'Send alert'}
          </Button>
        </>} data-id="element-4139" data-mp="3beorfc22c3jn">
      <div className="space-y-5" data-id="element-4142" data-mp="3v1xtk5ddo0t">
        {/* The number first. Nothing on this screen is a substitute for it. */}
        <div className="rounded-card border border-danger-100 bg-danger-50 px-4 py-3.5" data-id="element-4143" data-mp="37dv73kr47qp6">
          <p className="flex items-center gap-2 text-body font-semibold text-ink" data-id="element-4144" data-mp="32f1sfp03dtnf">
            <PhoneIcon aria-hidden className="h-4 w-4 text-danger-700" data-id="element-4145" data-mp="32cxgcjs1hoc2" />
            Call first if you can
          </p>
          <p className="cz-num mt-1 text-body-lg font-semibold text-ink" data-id="element-4146" data-mp="3cd7ty5hzkxrr">
            {responder?.phone ?? '112'}
          </p>
          <p className="mt-1 max-w-[62ch] text-body-sm text-ink" data-id="element-4147" data-mp="2omtx81ogiaqu">
            {responder ? `${responder.unit}. ${responder.hours}.` : 'National emergency line.'} A
            phone call reaches a person faster than any app. Use this alert as well, not instead.
          </p>
        </div>

        <div className={`rounded-card border px-4 py-3.5 ${unreachable ? 'border-warn-100 bg-warn-50' : 'border-line bg-canvas'}`} data-id="element-4148" data-mp="13xq92xht8ux6">
          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-4149" data-mp="u7esaad12r8v">
            {unreachable ? <TriangleAlertIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-4150" data-mp="1hwpvrzlrizqp" /> : <ShieldAlertIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-4151" data-mp="1mwc9091lcfzi" />}
            Where this goes
            {responder && <Badge tone={responder.channel === 'console' ? 'ok' : unreachable ? 'warn' : 'brand'} data-id="element-4152" data-mp="18ydwzaaqsvn6">
                {responder.channel === 'console' ? 'Connected' : responder.channel === 'relay' ? 'Relayed by phone' : 'Not connected'}
              </Badge>}
          </p>
          <p className="mt-1 max-w-[64ch] text-body-sm text-ink" data-id="element-4153" data-mp="244d4htsh84rk">
            {unreachable ? `Campuzon has no response agreement with ${responder?.unit ?? 'security on this campus'} yet. Your alert will be recorded and visible to you, but nobody will be notified. Call the number above.` : responder!.channel === 'console' ? `${responder!.unit} works this desk directly. Alerts here are typically opened within ${responder!.medianAckMinutes} minutes — measured from real alerts, not a target.` : `${responder!.unit} does not work the desk. Campuzon relays your alert to their control room, where a person forwards it. Median acknowledgement so far: ${responder!.medianAckMinutes} minutes.`}
          </p>
        </div>

        <ChoiceGroup label="What is happening?" options={KINDS.map(item => item.label)} value={kind} onChange={setKind} size="sm" data-id="element-4154" data-mp="35va6iwz37jqy" />

        <Field label="Where are you?" htmlFor="alert-place" hint="A landmark helps more than coordinates. Building, gate, hall or road." data-id="element-4155" data-mp="39cxsiaxfnlrk">
          <Input id="alert-place" value={placeHint} onChange={event => setPlaceHint(event.target.value)} placeholder="Behind Moremi Hall, near the generator house" data-id="element-4156" data-mp="3dbwxfvj5zij0" />
        </Field>

        <div data-id="element-4157" data-mp="2qelhejmyo1tc">
          <label htmlFor="alert-note" className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-4158" data-mp="2qf52283zx4b2">
            Anything they should know before arriving
          </label>
          <Textarea id="alert-note" rows={3} value={note} onChange={event => setNote(event.target.value)} data-id="element-4159" data-mp="2zdyvjpm15eke" />
        </div>

        <div className="space-y-1 rounded-card border border-line bg-canvas px-4 py-3" data-id="element-4160" data-mp="eo9fxadi5oqn">
          <p className="text-body font-semibold text-ink" data-id="element-4161" data-mp="eqneht7e5j2y">What will be shared</p>
          <Toggle checked={shareLocation} onChange={value => {
          setShareLocation(value);
          if (value) setLocating('idle');
        }} label="My precise location" description={locating === 'locating' ? 'Getting your location…' : locating === 'found' && coordinates ? `Found, accurate to about ${coordinates.accuracyMetres} m.` : locating === 'denied' ? 'Your device refused. Your landmark above will be sent instead.' : locating === 'unsupported' ? 'This device cannot provide a location. Your landmark will be sent instead.' : 'Only for this alert. Campuzon does not track you the rest of the time.'} data-id="element-4162" data-mp="jpgqfh715210" />
          <Toggle checked={shareContacts} onChange={setShareContacts} label={`Notify my ${trustedContacts.length} trusted contact${trustedContacts.length === 1 ? '' : 's'}`} description={trustedContacts.length === 0 ? 'You have not added any. Add them in Support before you need them.' : trustedContacts.map(contact => contact.name).join(', ')} data-id="element-4163" data-mp="32u88prom7ow" />
          <p className="flex items-start gap-2 border-t border-line pt-2.5 text-caption text-ink-subtle" data-id="element-4164" data-mp="24z34mx6we2v9">
            {locating === 'found' ? <MapPinIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-4165" data-mp="1qy8wmfnri3x4" /> : locating === 'locating' ? <LoaderIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 animate-spin" data-id="element-4166" data-mp="1hz5a11pdz6dv" /> : <CheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-4167" data-mp="1m0qqsy51k4tc" />}
            Nothing else is shared. Responders never see your notes, diary, results, balances or
            messages through an alert.
          </p>
        </div>
      </div>
    </Modal>;
}